import { Checkbox, Input, showToast } from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  useCreateTimeGridEntryMutation,
  useTimetableCardsCountForTimeGridEntryQuery,
  useUpdateTimeGridEntryMutation,
} from '../../../types/planung-graphql-client-defs';
import { convertMinsToHrsMins, timeStringToDate } from '../../../utils/helper';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useTimeGridEntries } from '../graphql/hooks/useTimeGridEntries';
import { TimeGridEntryFormValues } from '../graphql/types';
import { itemSchema } from './validation/schema';

type TimeGridEntryFormProps = {
  entryUuid: string;
  handleClose: () => void;
  gridUuid: string;
};

export const TimeGridEntryForm = observer(({ entryUuid, handleClose, gridUuid }: TimeGridEntryFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('TIME_GRID');

  const [loading, setLoading] = useState<boolean>(false);

  const [, create] = useCreateTimeGridEntryMutation();
  const [, update] = useUpdateTimeGridEntryMutation();

  const timeGridEntries = useTimeGridEntries(gridUuid);

  const [{ data: cardsData }] = useTimetableCardsCountForTimeGridEntryQuery({
    variables: { timeGridEntryUuid: entryUuid ?? '' },
    pause: !entryUuid,
    requestPolicy: 'cache-and-network',
  });

  const disableCriticalFields = cardsData ? cardsData.timetableCardsCountForTimeGridEntry > 0 : false;

  const current = useMemo(() => {
    const c = timeGridEntries?.find((tge) => {
      return tge.uuid === entryUuid;
    });
    return c ?? null;
  }, [timeGridEntries, entryUuid]);

  const other = useMemo(() => {
    const o = timeGridEntries?.filter((tge) => {
      return tge.uuid !== entryUuid;
    });
    return o ?? [];
  }, [timeGridEntries, entryUuid]);

  const start = dayjs(
    current
      ? current.start
      : timeGridEntries && timeGridEntries.length > 0
        ? timeGridEntries[timeGridEntries.length - 1].end
        : null,
  );

  const end = current?.end ? dayjs(current?.end) : null;

  const initialValue: TimeGridEntryFormValues = {
    name: current?.name ?? '',
    shortName: current?.shortName ?? '',
    start: start,
    end: end,
    order: current?.order ?? 0,
    reliable: current?.reliable ?? false,
    pause: current?.pause ?? false,
    print: current?.print ?? false,
  };

  const handleSubmit = async (values: TimeGridEntryFormValues, formHelpers: FormikHelpers<TimeGridEntryFormValues>) => {
    setLoading(true);
    let mutationResult;

    if (entryUuid) {
      mutationResult = await update(
        {
          uuid: entryUuid,
          update: {
            name: values.name,
            shortName: values.shortName,
            start: values.start.toISOString(),
            end: values.end ? values.end.toISOString() : null,
            reliable: values.reliable,
            pause: values.pause,
            print: values.print,
          },
        },
        context,
      );
    } else {
      mutationResult = await create(
        {
          input: {
            timeGridUuid: gridUuid,
            name: values.name,
            shortName: values.shortName,
            start: values.start.toISOString(),
            // end ist im Create-Input required; itemSchema erzwingt es bereits im Formular
            end: values.end?.toISOString() ?? '',
            reliable: values.reliable,
            pause: values.pause,
            print: values.print,
          },
        },
        context,
      );
    }
    if (!mutationResult || mutationResult.error) {
      showToast('Ups.', { type: 'error' });
    } else {
      showToast('Success', { type: 'success' });
    }
    formHelpers.resetForm();
    setLoading(false);
    handleClose();
  };

  return (
    <>
      <Formik validationSchema={itemSchema} initialValues={initialValue} enableReinitialize onSubmit={handleSubmit}>
        {(formik) => {
          const values = formik.values;
          const errors = formik.errors;
          const touched = formik.touched;
          const differenceInMinutes = values.end ? dayjs(values.end).diff(values.start, 'm') : 0;

          const validateTime = (value: Dayjs | null, isStart: boolean): string | null => {
            // Formik ruft validate auch mit leerem Feld auf; das Pflichtfeld deckt itemSchema ab
            if (!value) return null;
            if (isStart) {
              if (values.end && value.isAfter(values.end)) {
                return t('validation.common.startBeforeEnd');
              }
            } else {
              if (values.start && value.isBefore(values.start)) {
                return t('validation.common.startBeforeEnd');
              }
            }
            for (let i = 0; i < other.length; i++) {
              const startDate = new Date(other[i].start);
              const endDate = new Date(other[i].end);
              if (value.isAfter(startDate) && value.isBefore(endDate)) {
                return t('validation.timeGrid.noOverlapping');
              }
            }
            return null;
          };
          return (
            <Form>
              <div className='tks__grid mt-6 mb-10 no-gap'>
                <div className='tks__col col-xs-10 small-gap'>
                  <div className='tks__row small-gap'>
                    <div className='tks__col col-xs-6'>
                      <Input
                        value={values.name ?? ''}
                        name={'name'}
                        onChange={formik.handleChange}
                        label={t('common.name')}
                        error={errors.name}
                      />
                    </div>
                    <div className='tks__col col-xs-6'>
                      <Input
                        value={values.shortName ?? ''}
                        name={'shortName'}
                        onChange={formik.handleChange}
                        label={t('timeGrid.item.shortName') as string}
                        error={errors.shortName}
                      />
                    </div>
                  </div>
                  <div className='tks__row small-gap'>
                    <div className='tks__col col-xs-3'>
                      <Field
                        disabled={disableCriticalFields}
                        type={'time'}
                        validate={(value: Dayjs | null) => {
                          return validateTime(value, true);
                        }}
                        name={`start`}
                        label={t('timeGrid.item.start') as string}
                        error={touched.start ? (errors.start as string) : undefined}
                        value={values.start.format('HH:mm')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          formik.setFieldValue('start', timeStringToDate(event.target.value), true);
                          formik.setFieldTouched('start', true);
                        }}
                        as={Input}
                      />
                    </div>
                    <div className='tks__col col-xs-3'>
                      <Field
                        disabled={disableCriticalFields}
                        type={'time'}
                        validate={(value: Dayjs | null) => {
                          return validateTime(value, false);
                        }}
                        name={`end`}
                        label={t('timeGrid.item.end') as string}
                        error={touched.end ? (errors.end as string) : undefined}
                        value={values.end ? values.end.format('HH:mm') : ''}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          formik.setFieldValue('end', timeStringToDate(event.target.value), true);
                          formik.setFieldTouched('end', true);
                        }}
                        as={Input}
                      />
                    </div>
                    <div className='tks__col col-xs-6'>
                      <Input
                        label={t('timeGrid.item.duration') as string}
                        name={`duration`}
                        value={differenceInMinutes > 0 ? convertMinsToHrsMins(differenceInMinutes) : '0'}
                        readonly={true}
                      />
                    </div>
                  </div>
                </div>
                <div className='tks__row row-xs-2'>
                  <div className='tks__col col-xs-12 mt-5 px-5 no-gap'>
                    <Checkbox
                      disabled={disableCriticalFields}
                      name={'pause'}
                      checked={values.pause ?? false}
                      label={t('timeGrid.item.isPause') as string}
                      onChange={formik.handleChange}
                    />
                    <Checkbox
                      name={'reliable'}
                      checked={values.reliable ?? false}
                      label={t('timeGrid.item.isReliable') as string}
                      onChange={formik.handleChange}
                    />
                    <Checkbox
                      name={'print'}
                      checked={values.print ?? false}
                      label={t('timeGrid.item.print') as string}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
              </div>
              <ModalBottomButtons
                isLoading={loading}
                closeButton={{
                  text: t('common.cancelChanges'),
                  callback: () => {
                    formik.resetForm();
                    handleClose();
                  },
                }}
                submitButton={{ disabled: formik.isSubmitting || !formik.dirty || !formik.isValid }}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
});
